import React from "react";
import { Layers, Compass, FlaskConical, Umbrella, Trees, BadgeCheck } from "lucide-react";
import { BRAND } from "../lib/brand";
import HeroCarousel from "../components/HeroCarousel";

const features = [
  { icon: Layers, title: "Full Core, Full Panel", text: "100% hardwood core on every sheet — no fillers, no shortcuts." },
  { icon: Compass, title: "Architect's 1st Choice", text: "Specified across high-end residential and commercial interiors." },
  { icon: FlaskConical, title: "Lab Tested", text: "Independently tested for bonding strength, density and finish." },
  { icon: Umbrella, title: "All Weather Resistant", text: "Engineered to stand up to monsoons, humidity and dry heat." },
  { icon: Trees, title: "100% Gurjan Face Veneer", text: "Premium Gurjan timber face for a uniform, beautiful grain." },
  { icon: BadgeCheck, title: "ISI Approved", text: "All products manufactured to ISI/BIS quality standards." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO CAROUSEL */}
      <HeroCarousel />

      {/* INTRO STRIP */}
      <section className="paper-grain">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Premium Plywood · Made in India
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3 leading-[1.05]">
                The strength of a king,<br />in every single layer.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-wood-700 leading-relaxed">
                {BRAND.tagline} For homes, kitchens, offices and bespoke
                interiors that deserve nothing less than the finest grain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STRENGTH BANNER */}
      <section className="bg-wood-50">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 img-zoom rounded-sm overflow-hidden border border-wood-200 shadow-sm">
              <img
                src={BRAND.images.strengthRulesPoster}
                alt="Sylva King Plywood — Strength that Rules"
                className="w-full h-auto object-contain bg-wood-50"
                data-testid="brand-poster-img"
              />
            </div>
            <div className="lg:col-span-5">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Strength that Rules
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3 leading-[1.05]">
                #1 Recommended<br />by Architects.
              </h2>
              <p className="mt-6 text-wood-700 leading-relaxed">
                Full core, full panel construction with 100% Gurjan face veneer,
                ISI-approved and lab tested for the toughest sites. The Sylva
                King mark is a promise of strength that rules every space it
                enters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-24 lg:py-32 bg-wood-100">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 img-zoom rounded-sm overflow-hidden border border-wood-200">
            <img
              src={BRAND.images.craftsman}
              alt="Craftsman at work"
              className="w-full h-[520px] object-cover"
              data-testid="story-image"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
              Our Story
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3 leading-[1.05]">
              A leader among<br />plywood brands.
            </h2>
            <div className="mt-6 space-y-5 text-wood-700 leading-relaxed">
              <p>
                Sylva King is one of India&apos;s most trusted names in premium
                plywood. We exist for builders, architects, carpenters and
                homeowners who refuse to settle for second-best — and we earn
                that trust one sheet at a time.
              </p>
              <p>
                Unlike products that move straight from press to market, every
                Sylva King panel is inspected by our quality team — checked
                for bonding strength, moisture content, density and finish —
                before it leaves the factory floor. That commitment is why
                architects across India specify our boards by name.
              </p>
              <p>
                From kitchens that survive years of steam, to wardrobes that
                stay perfectly straight, to commercial interiors that age
                gracefully — Sylva King is the grain you build a legacy on.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div data-testid="stat-experience">
                <div className="font-serif text-4xl text-gold-600">20+</div>
                <div className="text-xs uppercase tracking-widest text-wood-700 mt-1">Years of craft</div>
              </div>
              <div data-testid="stat-states">
                <div className="font-serif text-4xl text-gold-600">25+</div>
                <div className="text-xs uppercase tracking-widest text-wood-700 mt-1">States served</div>
              </div>
              <div data-testid="stat-checks">
                <div className="font-serif text-4xl text-gold-600">100%</div>
                <div className="text-xs uppercase tracking-widest text-wood-700 mt-1">Quality checked</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section className="bg-wood-100 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="max-w-2xl">
            <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
              Our Craft
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3">
              Premium plywood products,<br />pressed to perfection.
            </h2>
            <p className="mt-6 text-wood-700 leading-relaxed">
              Every sheet that carries the Sylva King mark is built to outlast the
              ordinary — engineered for builders who refuse compromise and
              homeowners who expect a finer finish.
            </p>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-12 gap-6 lg:gap-8">
            <div className="lg:col-span-7 img-zoom card-flat rounded-sm overflow-hidden bg-wood-50">
              <img
                src={BRAND.images.calibratedPlywood}
                alt="Sylva King Calibrated Plywood"
                className="w-full h-[420px] object-contain"
              />
            </div>
            <div className="lg:col-span-5 card-flat rounded-sm p-10 flex flex-col justify-between">
              <div>
                <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                  Signature Range
                </div>
                <h3 className="font-serif text-3xl text-wood-800 mt-3">
                  King&apos;s Reserve
                </h3>
                <p className="mt-4 text-wood-700 leading-relaxed">
                  Our flagship premium calibrated plywood — hand-selected
                  veneers, precision pressed, finished smooth and ready for the
                  most demanding interiors.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 img-zoom card-flat rounded-sm overflow-hidden">
              <img
                src={BRAND.images.downImage1}
                alt="Modern kitchen built with Sylva King plywood"
                className="w-full h-[320px] object-cover"
              />
            </div>
            <div className="lg:col-span-7 img-zoom card-flat rounded-sm overflow-hidden">
              <img
                src={BRAND.images.downImage2}
                alt="Elegant wooden living room interior"
                className="w-full h-[320px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Why Sylva King
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3 leading-[1.05]">
                Engineered with patience.<br />Finished with pride.
              </h2>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
              {features.map(({ icon: Icon, title, text }, i) => (
                <div
                  key={title}
                  data-testid={`feature-card-${i}`}
                  className="card-flat rounded-sm p-7"
                >
                  <Icon className="text-gold-600" size={22} />
                  <h3 className="font-serif text-xl text-wood-800 mt-5">{title}</h3>
                  <p className="text-sm text-wood-700 mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 bg-wood-50">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="max-w-3xl">
            <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
              In their words
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3 leading-[1.05]">
              Trusted by builders<br />and homeowners alike.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              {
                q: "We've used Sylva King for the last two projects — kitchens, wardrobes, the works. Zero warping, beautiful finish. My go-to plywood now.",
                n: "Rakesh Mehta",
                r: "Interior Contractor, Delhi",
              },
              {
                q: "As an architect I look at three things — strength, finish and consistency. Sylva King delivers on all three, every single time.",
                n: "Priya Shah",
                r: "Architect, Surat",
              },
              {
                q: "Got my entire home furniture made from Sylva King boards. Two monsoons in — still rock solid. Absolutely worth every rupee.",
                n: "Gaurav Oberoi",
                r: "Homeowner, Mumbai",
              },
            ].map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="card-flat rounded-sm p-8 bg-white"
              >
                <div className="text-gold-600 font-serif text-5xl leading-none">&ldquo;</div>
                <p className="mt-2 text-wood-700 leading-relaxed">{t.q}</p>
                <div className="mt-6 pt-5 border-t border-wood-200">
                  <div className="font-serif text-lg text-wood-800">{t.n}</div>
                  <div className="text-xs uppercase tracking-widest text-wood-500 mt-1">{t.r}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
